import { ApiEndpoints } from "../apiEndpoints";
import { requestApi } from "../apiService";
import { saveFile } from "@/lib/saveFile";
import { ListRecordingsResponse } from "../types/umbra/meet";

/** Matches the backend's default page size (hard max 100). */
export const RECORDINGS_PAGE_SIZE = 20;

/**
 * List a GROUP chat's Jibri recordings (ow2-chat-be #234/#237, Phase-5).
 *
 * MODERATOR-ONLY on the backend: it returns 403 for a non-moderator member and
 * 404 for a non-member, so the surfacing UI MUST gate on role (see
 * ChatDetailsTabs) rather than relying on this call to fail quietly.
 *
 * Uses the shared AUTHED `requestApi` (Bearer + 401-refresh interceptor) —
 * recordings are authenticated, unlike the PUBLIC guest-invite redeem which
 * deliberately uses a bare client.
 *
 * @param before Opaque ISO `endedAt` cursor from a previous page's `nextBefore`.
 */
export async function listRecordings(
  chatId: string,
  before?: string | null,
): Promise<ListRecordingsResponse> {
  const response = await requestApi({
    url: ApiEndpoints.MEET_RECORDINGS_LIST(chatId),
    method: "GET",
    params: {
      limit: RECORDINGS_PAGE_SIZE,
      ...(before ? { before } : {}),
    },
  });
  return response.data as ListRecordingsResponse;
}

/**
 * Parse the download filename from a `Content-Disposition` header, tolerating
 * both the plain `filename="…"` and the RFC 5987 `filename*=UTF-8''…` forms.
 * Returns null when no filename is present so the caller can fall back.
 */
export function filenameFromContentDisposition(
  header?: string | null,
): string | null {
  if (!header) {
    return null;
  }
  const star = /filename\*=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1]);
    } catch {
      // Malformed percent-encoding — fall through to the plain filename below.
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] ?? null;
}

/**
 * Download a recording as an authed streamed mp4 (ow2-chat-be #234/#237).
 *
 * A plain `<a href>` will NOT send the Bearer header, so we fetch the stream
 * through the AUTHED `requestApi` client as a blob and hand it to the shared
 * `saveFile` helper (native share sheet on iOS, silent anchor download
 * elsewhere). The server returns the RAW mp4 (never JSON, never a GCS redirect)
 * and never exposes the object path — the client only ever holds the opaque id
 * and the returned blob.
 */
export async function downloadRecording(id: string): Promise<void> {
  const response = await requestApi({
    url: ApiEndpoints.MEET_RECORDING_DOWNLOAD(id),
    method: "GET",
    responseType: "blob",
  });
  const blob = response.data as Blob;
  const filename =
    filenameFromContentDisposition(
      response.headers?.["content-disposition"] as string | undefined,
    ) ?? `recording-${id}.mp4`;
  await saveFile(blob, filename);
}
